// client/src/components/unidades/tabs/UnidadeConfigTab.js
import React, { useState, useEffect } from 'react';
import useAgendas from '../../../hooks/useAgendas';
import useAlert from '../../../hooks/useAlert';
import { Save, Loader2, Calendar, Lock, XCircle, Settings } from 'lucide-react';

const extractIdStr = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    const id = obj._id || obj.id;
    return id ? String(id) : null;
};

const PrazoInput = ({ prazo, onPrazoChange, disabled }) => (
    <div className="input-group flex">
        <input 
            type="number" 
            name="valor" 
            value={prazo?.valor || ''} 
            onChange={onPrazoChange} 
            disabled={disabled}
            className="form-input flex-1" 
            min="0"
            style={{ borderTopRightRadius: 0, borderBottomRightRadius: 0 }}
        />
        <select 
            name="unidade" 
            value={prazo?.unidade || 'horas'} 
            onChange={onPrazoChange} 
            disabled={disabled}
            className="form-input" 
            style={{ borderTopLeftRadius: 0, borderBottomLeftRadius: 0, width: '120px' }}
        >
            <option value="minutos">Minutos</option>
            <option value="horas">Horas</option>
            <option value="dias">Dias</option>
        </select>
    </div>
);

const UnidadeConfigTab = ({ unidade, onUpdate }) => {
    const { setAlert } = useAlert();
    const unidadeIdValido = extractIdStr(unidade);
    
    const { useAgendaByEntidadeQuery, updateAgenda } = useAgendas();
    const { data: agendaResponse, isLoading: loadingAgenda } = useAgendaByEntidadeQuery('Unidade', unidadeIdValido, {
        enabled: !!unidadeIdValido
    });

    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    
    const [formData, setFormData] = useState({
        nomeExibicao: '',
        permiteAgendamentoOnline: true,
        intervaloGradeHorarios: 60,
        horarioInicio: 7,
        horarioFim: 22,
        prazoCancelamentoSemPagamento: { valor: 24, unidade: 'horas' },
        acaoCancelamentoSemPagamento: 'Ser Avisado',
        disponibilidadeFuturaEmDias: 30,
        antecedenciaMinimaParaAgendar: { valor: 1, unidade: 'horas' },
        permiteCancelamentoOnline: 'Automático',
        prazoMaximoCancelamentoOnline: { valor: 24, unidade: 'horas' },
        visibilidade: 'publica_na_unidade', 
        unidadesVinculadas: [], 
        corAgenda: '#3788d8',
        exibirNaAgenda: true
    });

    useEffect(() => {
        if (loadingAgenda) return;
        
        const agendaData = agendaResponse?.data || agendaResponse;
        const fallbackName = unidade?.nome || 'Unidade';

        if (agendaData && (agendaData.id || agendaData._id)) {
            const agendaInfo = agendaData.configuracoes || {};
            const permissoesInfo = agendaData.permissoes || {};
            const metaInfo = agendaData.metadata || {};

            setFormData({
                nomeExibicao: agendaData.nomeExibicao || `Agenda de ${fallbackName}`,
                permiteAgendamentoOnline: agendaInfo.permiteAgendamentoOnline !== false,
                intervaloGradeHorarios: Number(agendaInfo.intervaloGradeHorarios) || 60,
                horarioInicio: Number(agendaInfo.horarioInicio) ?? 7,
                horarioFim: Number(agendaInfo.horarioFim) ?? 22,
                
                prazoCancelamentoSemPagamento: {
                    valor: Number(agendaInfo.prazoCancelamentoSemPagamento?.valor) || 0,
                    unidade: agendaInfo.prazoCancelamentoSemPagamento?.unidade || 'horas'
                },
                acaoCancelamentoSemPagamento: agendaInfo.acaoCancelamentoSemPagamento || 'Ser Avisado',
                disponibilidadeFuturaEmDias: Number(agendaInfo.disponibilidadeFuturaEmDias) || 30,
                antecedenciaMinimaParaAgendar: {
                    valor: Number(agendaInfo.antecedenciaMinimaParaAgendar?.valor) || 1,
                    unidade: agendaInfo.antecedenciaMinimaParaAgendar?.unidade || 'horas'
                },
                permiteCancelamentoOnline: agendaInfo.permiteCancelamentoOnline || 'Automático',
                prazoMaximoCancelamentoOnline: {
                    valor: Number(agendaInfo.prazoMaximoCancelamentoOnline?.valor) || 0,
                    unidade: agendaInfo.prazoMaximoCancelamentoOnline?.unidade || 'horas'
                },
                visibilidade: permissoesInfo.visibilidade || 'publica_na_unidade',
                unidadesVinculadas: (permissoesInfo.unidadesVinculadas || []).map(extractIdStr),
                corAgenda: metaInfo.corAgenda || '#3788d8',
                exibirNaAgenda: metaInfo.exibirNaAgenda !== false
            });
        } else {
            setFormData(prev => ({
                ...prev,
                nomeExibicao: `Agenda de ${fallbackName}`,
                unidadesVinculadas: unidadeIdValido ? [unidadeIdValido] : []
            }));
        }
    }, [agendaResponse, loadingAgenda, unidade, unidadeIdValido]);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        let finalValue = value;
        
        if (type === 'checkbox') {
            finalValue = checked;
        } else if (type === 'number' || ['intervaloGradeHorarios'].includes(name)) {
            finalValue = value === '' ? 0 : Number(value);
            if (name === 'horarioInicio' || name === 'horarioFim') {
                if (finalValue < 0) finalValue = 0;
                if (finalValue > 23) finalValue = 23;
            }
        }
        
        setFormData(prev => ({ ...prev, [name]: finalValue }));
    };

    const onPrazoChange = (parentKey, e) => {
        const { name, value } = e.target;
        const valorFinal = name === 'valor' && value !== '' ? parseInt(value, 10) : value;
        setFormData(prev => ({ 
            ...prev, 
            [parentKey]: { 
                ...(prev[parentKey] || {}), 
                [name]: valorFinal 
            }
        }));
    };

    const onSubmit = async (e) => {
        e.preventDefault();
        try {
            if (!unidadeIdValido) {
                setAlert('ID da unidade não encontrado para atualizar a agenda', 'danger');
                return;
            }

            if (!formData.nomeExibicao?.trim()) {
                setAlert('O Nome de Exibição da agenda é obrigatório.', 'warning');
                return;
            }

            setIsSaving(true);

            const payload = {
                tipoEntidade: 'Unidade',
                entidadeId: unidadeIdValido,
                nomeExibicao: formData.nomeExibicao.trim(),
                configuracoes: {
                    permiteAgendamentoOnline: formData.permiteAgendamentoOnline,
                    intervaloGradeHorarios: Number(formData.intervaloGradeHorarios),
                    horarioInicio: Number(formData.horarioInicio),
                    horarioFim: Number(formData.horarioFim),
                    prazoCancelamentoSemPagamento: {
                        valor: Number(formData.prazoCancelamentoSemPagamento.valor),
                        unidade: formData.prazoCancelamentoSemPagamento.unidade
                    },
                    acaoCancelamentoSemPagamento: formData.acaoCancelamentoSemPagamento,
                    disponibilidadeFuturaEmDias: Number(formData.disponibilidadeFuturaEmDias),
                    antecedenciaMinimaParaAgendar: {
                        valor: Number(formData.antecedenciaMinimaParaAgendar.valor),
                        unidade: formData.antecedenciaMinimaParaAgendar.unidade
                    },
                    permiteCancelamentoOnline: formData.permiteCancelamentoOnline,
                    prazoMaximoCancelamentoOnline: {
                        valor: Number(formData.prazoMaximoCancelamentoOnline.valor),
                        unidade: formData.prazoMaximoCancelamentoOnline.unidade
                    }
                },
                permissoes: {
                    visibilidade: formData.visibilidade,
                    unidadesVinculadas: [unidadeIdValido] // Agenda de unidade só pertence a ela mesma
                },
                metadata: {
                    corAgenda: formData.corAgenda,
                    exibirNaAgenda: formData.exibirNaAgenda
                }
            };

            await updateAgenda.mutateAsync({
                entidadeId: unidadeIdValido,
                agendaData: payload
            });

            setAlert('Configurações da agenda salvas com sucesso!', 'success');
            setIsEditing(false);
            if (onUpdate) onUpdate();

        } catch (error) {
            console.error('❌ Erro ao salvar agenda da unidade:', error);
            const detailMsg = error.response?.data?.error || error.message;
            setAlert(`Erro ao salvar a agenda: ${detailMsg}`, 'danger');
        } finally {
            setIsSaving(false);
        }
    };

    if (loadingAgenda) {
        return (
            <div className="flex justify-center items-center h-64">
                <Loader2 className="animate-spin text-blue-600" size={40} />
            </div>
        );
    }

    return (
        <form onSubmit={onSubmit} noValidate className="form-content">
            {/* CABEÇALHO PADRONIZADO */}
            <div className="form-header flex items-center justify-between mb-6">
                <div>
                    <h3 className="form-title flex items-center mb-0">
                        <Calendar size={20} className="mr-2 text-primary" />
                        Configurações da Agenda
                    </h3>
                    <p className="text-sm text-gray-500 mt-1">
                        Defina horários, cor, cancelamentos e regras de agendamento desta unidade.
                    </p>
                </div>

                {!isEditing ? (
                    <button
                        type="button"
                        onClick={() => setIsEditing(true)}
                        className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                    >
                        <Settings size={16} className="icon text-gray-500" /> Editar Configurações
                    </button>
                ) : (
                    <div className="flex gap-2">
                        <button
                            type="button"
                            onClick={() => setIsEditing(false)}
                            className="action-button action-button-secondary bg-white border border-gray-300 shadow-sm text-gray-700"
                        >
                            <XCircle size={16} className="icon text-gray-500" /> Cancelar
                        </button>
                        <button
                            type="submit"
                            disabled={isSaving}
                            className="action-button action-button-primary bg-primary text-white shadow-sm"
                        >
                            {isSaving ? <Loader2 size={16} className="icon animate-spin" /> : <Save size={16} className="icon" />}
                            Salvar Configurações
                        </button>
                    </div>
                )}
            </div>

            <div className="form-tabs-content p-0">
                <fieldset className="form-section" disabled={!isEditing || isSaving}>
                    <legend className="form-section-title">
                        <Calendar size={18} className="inline mr-2"/>
                        Configurações Gerais e Exibição
                    </legend>
                    <div className="form-grid-container">
                        
                        <div className="form-group md:col-span-2 pb-4 mb-2 border-b border-gray-100">
                            <label className="required font-medium text-gray-800">Nome de Exibição da Agenda</label>
                            <input 
                                type="text" 
                                name="nomeExibicao" 
                                value={formData.nomeExibicao} 
                                onChange={handleChange} 
                                className="form-input mt-1 md:w-2/3"
                                placeholder='Ex: Agenda da Unidade Centro'
                                required
                            />
                            <p className="text-xs text-gray-500 mt-1">Este é o nome que aparecerá no calendário principal.</p>
                        </div>

                        <div className="form-group">
                            <label>Cor da Agenda</label>
                            <div className="flex items-center gap-2 mt-1">
                                <input 
                                    type="color" 
                                    name="corAgenda" 
                                    value={formData.corAgenda} 
                                    onChange={handleChange} 
                                    className="w-10 h-8 rounded cursor-pointer"
                                />
                                <span className="text-sm text-gray-600">
                                    {formData.corAgenda}
                                </span>
                            </div>
                        </div>

                        <div className="form-group md:col-span-2">
                            <label className="checkbox-label">
                                <input 
                                    type="checkbox" 
                                    name="exibirNaAgenda" 
                                    checked={formData.exibirNaAgenda} 
                                    onChange={handleChange} 
                                    className="form-checkbox"
                                />
                                <span className="checkbox-label-text">Exibir unidade na agenda principal da recepção</span>
                            </label>
                        </div>
                        
                        <div className="form-group md:col-span-2 mt-2">
                            <label className="font-medium text-gray-800">Visibilidade (Controle de Acesso ABAC)</label>
                            <select 
                                name="visibilidade" 
                                value={formData.visibilidade} 
                                onChange={handleChange} 
                                className="form-input md:w-1/2 mt-1"
                            >
                                <option value="publica_na_unidade">Pública na Unidade (Padrão)</option>
                                <option value="publica_na_conta">Pública em toda a Conta</option>
                                <option value="privada">Privada (Apenas equipe autorizada)</option>
                            </select>

                            {formData.visibilidade === 'publica_na_unidade' && (
                                <div className="mt-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
                                    <div className="flex flex-col gap-1 p-3 bg-blue-50 text-blue-800 rounded border border-blue-100">
                                        <div className="flex items-center font-medium">
                                            <Lock size={16} className="mr-2"/> Unidade Vinculada Automaticamente
                                        </div>
                                        <p className="text-sm">A agenda de uma unidade é exclusiva dela mesma e não pode ser vinculada manualmente a outras unidades.</p>
                                    </div>
                                </div>
                            )}
                        </div>

                        <div className="form-group md:col-span-2 mt-4 pt-4 border-t border-gray-100">
                            <label className="checkbox-label font-medium text-gray-800">
                                <input 
                                    type="checkbox" 
                                    name="permiteAgendamentoOnline" 
                                    id="permiteAgendamentoOnlineUser" 
                                    checked={formData.permiteAgendamentoOnline} 
                                    onChange={handleChange} 
                                    className="form-checkbox w-4 h-4" 
                                />
                                <label htmlFor="permiteAgendamentoOnlineUser" className="ml-2 font-medium text-gray-800 cursor-pointer">
                                    Permitir que clientes agendem sozinhos online
                                </label>
                                <p className="text-xs text-gray-500 ml-6 mt-1 cursor-default">Ao desativar, a unidade só poderá receber agendamentos pela recepção.</p>
                            </label>
                        </div>

                        <div className="form-group">
                            <label>Intervalo entre horários (minutos)</label>
                            <select 
                                name="intervaloGradeHorarios" 
                                value={formData.intervaloGradeHorarios} 
                                onChange={handleChange} 
                                className="form-input mt-1"
                            >
                                <option value="10">10 min</option>
                                <option value="15">15 min</option>
                                <option value="20">20 min</option>
                                <option value="30">30 min</option>
                                <option value="40">40 min</option>
                                <option value="45">45 min</option>
                                <option value="60">1 hora</option>
                            </select>
                        </div>

                        <div className="form-group">
                            <label>Dias de disponibilidade futura</label>
                            <input 
                                type="number" 
                                name="disponibilidadeFuturaEmDias" 
                                value={formData.disponibilidadeFuturaEmDias} 
                                onChange={handleChange} 
                                className="form-input mt-1" 
                                min="1" 
                                max="365"
                            />
                            <p className="form-hint mt-1">Quantos dias no futuro o cliente pode ver a agenda.</p>
                        </div>

                        <div className="form-group">
                            <label>Início do expediente (Hora)</label>
                            <input 
                                type="number" 
                                name="horarioInicio" 
                                value={formData.horarioInicio} 
                                onChange={handleChange} 
                                className="form-input mt-1" 
                                min="0" 
                                max="23"
                            />
                        </div>

                        <div className="form-group">
                            <label>Fim do expediente (Hora)</label>
                            <input 
                                type="number" 
                                name="horarioFim" 
                                value={formData.horarioFim} 
                                onChange={handleChange} 
                                className="form-input mt-1" 
                                min="0" 
                                max="23"
                            />
                        </div>

                        <div className="form-group md:col-span-2 md:w-1/2">
                            <label className="block mb-1">Antecedência mínima para agendar</label>
                            <PrazoInput 
                                prazo={formData.antecedenciaMinimaParaAgendar} 
                                onPrazoChange={(e) => onPrazoChange('antecedenciaMinimaParaAgendar', e)} 
                                disabled={!isEditing}
                            />
                            <p className="form-hint mt-1">Quanto tempo antes o cliente deve marcar.</p>
                        </div>
                    </div>
                </fieldset>

                <fieldset className="form-section mt-6" disabled={!isEditing || isSaving}>
                    <legend className="form-section-title">Regras de Cancelamento e Prazos</legend>
                    <div className="form-grid-container">
                        <div className="form-group md:col-span-2 pb-4">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                
                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Cancelamento pelo cliente</label>
                                        <select 
                                            name="permiteCancelamentoOnline" 
                                            value={formData.permiteCancelamentoOnline} 
                                            onChange={handleChange} 
                                            className="form-input"
                                        >
                                            <option value="Não Permitir">Não Permitir Cancelamento</option>
                                            <option value="Aprovação Necessária">Requer Aprovação</option>
                                            <option value="Automático">Automático</option>
                                        </select>
                                    </div>

                                    {formData.permiteCancelamentoOnline !== 'Não Permitir' && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1">Antecedência para cancelar online</label>
                                            <PrazoInput 
                                                prazo={formData.prazoMaximoCancelamentoOnline} 
                                                onPrazoChange={(e) => onPrazoChange('prazoMaximoCancelamentoOnline', e)} 
                                                disabled={!isEditing}
                                            />
                                            <p className="text-xs text-gray-500 mt-1">O cliente só pode cancelar se for antes deste prazo.</p>
                                        </div>
                                    )}
                                </div>

                                <div className="space-y-4">
                                    <div>
                                        <label className="text-sm font-medium text-gray-700 block mb-1">Ação para Agendamentos Pendentes de Pgto</label>
                                        <select 
                                            name="acaoCancelamentoSemPagamento" 
                                            value={formData.acaoCancelamentoSemPagamento} 
                                            onChange={handleChange} 
                                            className="form-input"
                                        >
                                            <option value="Ser Avisado">Manter na Agenda e Avisar Recepção</option>
                                            <option value="Cancelar Automaticamente">Cancelar Automaticamente</option>
                                        </select>
                                    </div>
                                    
                                    {formData.acaoCancelamentoSemPagamento === 'Cancelar Automaticamente' && (
                                        <div>
                                            <label className="text-sm font-medium text-gray-700 block mb-1">Tempo limite sem pagamento</label>
                                            <PrazoInput 
                                                prazo={formData.prazoCancelamentoSemPagamento} 
                                                onPrazoChange={(e) => onPrazoChange('prazoCancelamentoSemPagamento', e)} 
                                                disabled={!isEditing}
                                            />
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>
                </fieldset>
            </div>
        </form>
    );
};

export default UnidadeConfigTab;